/**
 * Typed error classes used across source modules. The reader/UI layer
 * pattern-matches `instanceof` to decide whether to show a re-auth banner,
 * a Cloudflare retry button, a 509 rate-limit explainer, etc.
 */

export class SourceError extends Error {
  readonly source?: string;
  constructor(message: string, opts?: { source?: string; cause?: unknown }) {
    super(message);
    this.name = new.target.name;
    if (opts?.source !== undefined) this.source = opts.source;
    if (opts?.cause !== undefined) {
      (this as { cause?: unknown }).cause = opts.cause;
    }
  }
}

/**
 * Cloudflare bot/JS challenge surfaced via `cf-mitigated: challenge` or
 * the well-known `__cf_chl_*` HTML markers. The user typically needs to
 * refresh cookies on a real browser.
 */
export class CloudflareChallenge extends SourceError {}

/** Source returned a logged-out-shape response despite the user having creds. */
export class AuthExpired extends SourceError {}

/** exhentai-specific: server returns an empty body. The user lacks access. */
export class SadPanda extends SourceError {}

/** e-hentai 509 image rate-limit. The user has hit their daily image quota. */
export class QuotaExceeded extends SourceError {}

/**
 * The upstream temporarily rate-limited the network path used for a source
 * request. For proxied HTML this is the Worker's shared egress IP; direct
 * API requests are instead limited against the user's own network address.
 *
 * Distinct from {@link QuotaExceeded} (a genuine per-account/IP image quota
 * or 509 placeholder) because upgrading the account does not clear either
 * temporary request-rate limit. Subclasses {@link QuotaExceeded} so existing
 * `instanceof QuotaExceeded` handlers keep working; the reader banner uses
 * `egress` to give transport-specific guidance.
 */
export class RateLimited extends QuotaExceeded {
  /** Network path that the source rate-limited. Defaults to the proxy for legacy callers. */
  readonly egress?: 'proxy' | 'client';
  /** When the upstream ban is expected to lift (ms since epoch), if known. */
  readonly retryAt?: number;
  /** Raw upstream retry window text, e.g. "13 hours and 44 minutes". */
  readonly retryAfterText?: string;
  constructor(
    message: string,
    opts?: {
      source?: string;
      egress?: 'proxy' | 'client';
      retryAt?: number;
      retryAfterText?: string;
      cause?: unknown;
    },
  ) {
    super(message, opts);
    if (opts?.egress !== undefined) this.egress = opts.egress;
    if (opts?.retryAt !== undefined) this.retryAt = opts.retryAt;
    if (opts?.retryAfterText !== undefined) this.retryAfterText = opts.retryAfterText;
  }
}

/** HTML/JSON parse failures — the source likely changed its markup. */
export class ParseError extends SourceError {
  /** Optional snippet of the failing HTML/JSON for triage. */
  readonly snippet?: string;
  constructor(message: string, opts?: { source?: string; snippet?: string; cause?: unknown }) {
    super(message, opts);
    if (opts?.snippet !== undefined) this.snippet = opts.snippet;
  }
}

/** Lower-level networking / proxy / DNS / abort failures. */
export class NetworkError extends SourceError {
  readonly status?: number;
  constructor(message: string, opts?: { source?: string; status?: number; cause?: unknown }) {
    super(message, opts);
    if (opts?.status !== undefined) this.status = opts.status;
  }
}
